import React, { useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { CheckCircle, XCircle, ArrowRight, Target, Zap, TrendingUp, Users, Video, FileText, Edit3, Image as ImageIcon, Package, Search } from 'lucide-react';
import { mockData } from '../data/mock';

const CALENDLY_URL = "https://calendly.com/daizenmediaa/30min";
const CALENDLY_CSS_URL = "https://assets.calendly.com/assets/external/widget.css";
const CALENDLY_JS_URL = "https://assets.calendly.com/assets/external/widget.js";

const Landing = () => {
  useEffect(() => {
    // Inject Calendly stylesheet
    if (!document.querySelector(`link[href="${CALENDLY_CSS_URL}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = CALENDLY_CSS_URL;
      document.head.appendChild(link);
    }
    // Inject Calendly widget script
    if (!document.querySelector(`script[src="${CALENDLY_JS_URL}"]`)) {
      const script = document.createElement('script');
      script.src = CALENDLY_JS_URL;
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  const handleBooking = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (window.Calendly && typeof window.Calendly.initPopupWidget === 'function') {
      window.Calendly.initPopupWidget({ url: CALENDLY_URL });
    } else {
      // Fallback if the widget script hasn't loaded yet
      window.open(CALENDLY_URL, '_blank');
    }
  };

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="logo">DaizenLabs</div>
          <Button className="cta-button" onClick={handleBooking}>Get Started</Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-headline">
              {mockData.hero.preHeadline}
            </h1>
            <h2 className="hero-subheadline">
              {mockData.hero.headline}
            </h2>
            <p className="hero-description">
              {mockData.hero.subheadline}
            </p>

            <div className="hero-video-wrapper">
              <div className="hero-video-container">
                <iframe
                  src="https://www.youtube.com/embed/ykkDGI2ORR8?start=23"
                  title="DaizenLabs Overview"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>
            </div>

            <Button className="hero-cta-button" size="lg" onClick={handleBooking}>
              Get Started Now
              <ArrowRight className="ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="problem-section">
        <div className="container">
          <h2 className="section-title">I'm Not Throwing Shade At You But...</h2>
          <h3 className="section-subtitle">
            You're a Business Owner, Not a YouTuber
          </h3>
          <p className="problem-description">
            Let's be real - the problem isn't that you don't believe in YouTube. You already know it's the move. 
            You see the channels. You've watched your competitors blow up. You've probably even tried it yourself. 
            But is it working for you like it's working for them?
          </p>

          <h3 className="problems-headline">
            The way to make YouTube work, isn't to:
          </h3>

          <div className="problems-grid">
            {mockData.problems.map((problem, index) => (
              <Card key={index} className="problem-card">
                <CardContent className="problem-content">
                  <XCircle className="problem-icon" />
                  <h4 className="problem-title">{problem.title}</h4>
                  <p className="problem-description">{problem.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="container">
          <h2 className="section-title">Here's How We Make Your YouTube Print...</h2>
          <h3 className="section-subtitle">
            Here's what you can expect with just one video a week, done right.
          </h3>
          <p className="benefits-intro">
            You don't need to become a YouTuber. You don't need a massive audience. 
            You don't need to reinvent your content strategy. 
            <strong> Just one video per week, with the right strategy can...</strong>
          </p>

          <div className="benefits-grid">
            {mockData.benefits.map((benefit, index) => (
              <Card key={index} className="benefit-card">
                <CardContent className="benefit-content">
                  <CheckCircle className="benefit-icon" />
                  <h4 className="benefit-title">{benefit.title}</h4>
                  <p className="benefit-description">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="benefits-cta">
            <p className="benefits-cta-text">
              Done right, one YouTube video per week can book you <strong>10–30 qualified, inbound sales calls per week</strong>, 
              with leads who show up already trusting you
            </p>
            <Button className="cta-button" size="lg" onClick={handleBooking}>
              Get Started Now
            </Button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section">
        <div className="container">
          <h2 className="section-title">Here's Our Offer To You...</h2>
          <h3 className="section-subtitle">
            We'll Run Everything For You... All You Do Is Yap In Front Of a Camera Once-Per-Week
          </h3>
          <p className="services-intro">We handle everything completely done-for-you...</p>

          <div className="services-image-container">
            <img 
              src="https://customer-assets.emergentagent.com/job_88ee96ee-5bfc-41f5-9fd8-df6f287453a0/artifacts/gmmn1o1i_b4517bfc5ef638f859ddfe10c5c524e6.avif" 
              alt="Services Overview" 
              className="services-image"
            />
          </div>

          <div className="services-grid">
            {mockData.services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card key={index} className="service-card">
                  <CardContent className="service-content">
                    <div className="service-icon-wrapper">
                      <IconComponent className="service-icon" />
                    </div>
                    <h4 className="service-title">{service.title}</h4>
                    <p className="service-description">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Button className="cta-button" size="lg" onClick={handleBooking}>
              Get Started Now
            </Button>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="comparison-section">
        <div className="container">
          <h2 className="section-title comparison-title">YouTube = The King 👑</h2>
          <h3 className="section-subtitle">
            Leads That Watch Your YouTube Videos Before Sales Calls Simply Show Up & Close WAY Easier...
          </h3>

          <div className="comparison-grid">
            <Card className="comparison-card comparison-bad">
              <CardContent className="comparison-content">
                <h4 className="comparison-heading">Other Acquisition Channels</h4>
                <ul className="comparison-list">
                  {mockData.comparison.without.map((item, index) => (
                    <li key={index} className="comparison-item">
                      <XCircle className="comparison-icon-bad" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="comparison-card comparison-good">
              <CardContent className="comparison-content">
                <h4 className="comparison-heading">Building a YouTube Funnel</h4>
                <ul className="comparison-list">
                  {mockData.comparison.with.map((item, index) => (
                    <li key={index} className="comparison-item">
                      <CheckCircle className="comparison-icon-good" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <Button className="cta-button" size="lg" onClick={handleBooking}>
              Get Started Now
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>

          <Accordion type="single" collapsible className="faq-accordion">
            {mockData.faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="faq-item">
                <AccordionTrigger className="faq-question">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="faq-answer">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta-section">
        <div className="container">
          <h2 className="final-cta-title">Still Scrolling?</h2>
          <h3 className="final-cta-subtitle">
            What Other Information Could You Possibly Need?
          </h3>
          <p className="final-cta-text">
            Click below, book a call, and turn YouTube into your number 1 inbound acquisition channel now...
          </p>
          <Button className="final-cta-button" size="lg" onClick={handleBooking}>
            Get Started Now
            <ArrowRight className="ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <p className="footer-text">DaizenLabs 2025</p>
          <p className="footer-disclaimer">
            This site is not a part of the Facebook™ website or Meta Platforms, Inc. 
            Additionally, this site is NOT endorsed by Facebook™ in any way. Facebook™ is a trademark of Meta Platforms, Inc.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;